import type { Schedule, ScheduleRecurrence } from '@/types/api'

/**
 * Time-rule helpers (SCHEDULING.md §2.3, §3): ISO weekday bitmask, time parsing with the
 * `24:00` end, overnight detection and the human-readable rule summary.
 */

export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
export const WEEKDAY_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const
export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

export const ALL_DAYS = 127
export const WEEKDAYS_MASK = 31
export const WEEKEND_MASK = 96

/* ------------------------------------------------------------------ weekdays */

/** Bitmask → ISO weekday indexes (0 = Monday … 6 = Sunday). */
export function maskToWeekdays(mask: number): number[] {
  const out: number[] = []
  for (let i = 0; i < 7; i++) if (mask & (1 << i)) out.push(i)
  return out
}

/** ISO weekday indexes → bitmask (out-of-range indexes are ignored). */
export function weekdaysToMask(days: readonly number[]): number {
  let mask = 0
  for (const d of days) if (Number.isInteger(d) && d >= 0 && d <= 6) mask |= 1 << d
  return mask
}

export function toggleWeekday(mask: number, day: number): number {
  return (mask ^ (1 << day)) & ALL_DAYS
}

export function hasWeekday(mask: number, day: number): boolean {
  return (mask & (1 << day)) !== 0
}

/** "Every day", "Weekdays", "Weekends", or "Mon, Wed, Fri" (consecutive runs collapse: "Mon–Thu"). */
export function weekdaysLabel(mask: number): string {
  const m = mask & ALL_DAYS
  if (m === ALL_DAYS) return 'every day'
  if (m === WEEKDAYS_MASK) return 'weekdays'
  if (m === WEEKEND_MASK) return 'weekends'
  if (m === 0) return 'no days'
  const days = maskToWeekdays(m)
  const runs: number[][] = []
  for (const d of days) {
    const last = runs[runs.length - 1]
    if (last && last[last.length - 1] === d - 1) last.push(d)
    else runs.push([d])
  }
  return runs.map((r) => (r.length >= 3 ? `${WEEKDAY_SHORT[r[0]!]}–${WEEKDAY_SHORT[r[r.length - 1]!]}` : r.map((d) => WEEKDAY_SHORT[d]).join(', '))).join(', ')
}

/* --------------------------------------------------------------------- time */

export const DAY_MINUTES = 1440

/** `HH:MM[:SS]` → minutes after midnight; `24:00` only when `allow24`. null when malformed. */
export function parseTime(value: string | null | undefined, allow24 = false): number | null {
  if (value === null || value === undefined || value === '') return null
  const m = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  const sec = Number(m[3] ?? 0)
  if (h === 24 && min === 0 && sec === 0) return allow24 ? DAY_MINUTES : null
  if (h > 23 || min > 59 || sec > 59) return null
  return h * 60 + min
}

/** Minutes → `HH:MM`; 1440 → `24:00`. */
export function formatTime(minutes: number): string {
  const m = Math.max(0, Math.min(DAY_MINUTES, Math.round(minutes)))
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

export interface TimeWindow {
  /** Minutes after local midnight. */
  start: number
  /** Minutes after the occurrence day's midnight; `<= start` means the window ends next day. */
  end: number
  crossing: boolean
  allDay: boolean
}

/** SCHEDULING.md §3.1 normalisation: null/`00:00` end = end of day; `end <= start` crosses midnight. */
export function normalizeWindow(startTime: string | null | undefined, endTime: string | null | undefined): TimeWindow {
  const start = parseTime(startTime) ?? 0
  let end = parseTime(endTime, true) ?? DAY_MINUTES
  if (end === 0) end = DAY_MINUTES
  const crossing = end <= start
  return { start, end, crossing, allDay: start === 0 && end === DAY_MINUTES }
}

/** Window length in minutes (24 h for `08:00–08:00`). */
export function windowMinutes(w: TimeWindow): number {
  return w.crossing ? DAY_MINUTES - w.start + w.end : w.end - w.start
}

/** "09:00–17:00", "22:00–02:00 (overnight)", "20:00–24:00", "all day", "08:00–08:00 (24 h)". */
export function windowLabel(startTime: string | null | undefined, endTime: string | null | undefined): string {
  const w = normalizeWindow(startTime, endTime)
  if (w.allDay) return 'all day'
  const range = `${formatTime(w.start)}–${formatTime(w.end)}`
  if (!w.crossing) return range
  return w.end === w.start ? `${range} (24 h)` : `${range} (overnight)`
}

/** Editor hint for the end time, or null when nothing special. */
export function overnightHint(startTime: string | null | undefined, endTime: string | null | undefined): string | null {
  const w = normalizeWindow(startTime, endTime)
  if (w.allDay) return null
  if (!w.crossing) return w.end === DAY_MINUTES ? 'Runs until midnight.' : null
  if (w.end === w.start) return 'Start and end are equal: a 24-hour window ending at the same time the next day.'
  return `Overnight: ends at ${formatTime(w.end)} the next day. The whole window belongs to the day it starts.`
}

/* -------------------------------------------------------------------- dates */

/** `2026-10-08` → `8 Oct 2026` (locale-independent so summaries are stable). */
export function formatRuleDate(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!m) return date
  return `${Number(m[3])} ${MONTH_SHORT[Number(m[2]) - 1] ?? m[2]} ${m[1]}`
}

/** `2026-10-08` → ISO weekday index (0 = Monday). */
export function isoWeekdayOf(date: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!m) return null
  const day = Math.floor(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86_400_000)
  return (((day + 3) % 7) + 7) % 7
}

function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')
  return `${n}${s}`
}

export function daysOfMonthLabel(days: readonly number[] | null | undefined): string {
  const sorted = [...new Set(days ?? [])].sort((a, b) => a - b)
  if (sorted.length === 0) return 'no days'
  if (sorted.length <= 1) return `the ${ordinal(sorted[0]!)}`
  return `the ${sorted.slice(0, -1).map(ordinal).join(', ')} and ${ordinal(sorted[sorted.length - 1]!)}`
}

/* ------------------------------------------------------------------ summary */

export type RuleLike = Pick<Schedule, 'recurrence' | 'start_date' | 'end_date' | 'start_time' | 'end_time' | 'weekdays' | 'days_of_month' | 'timezone'>

export function timezoneLabel(timezone: string | null | undefined): string {
  return !timezone || timezone === 'display' ? 'display time' : `${timezone.replace(/_/g, ' ')} time`
}

/**
 * Human-readable rule: "Every weekday 09:00–17:00, display time",
 * "Once on Thu 8 Oct 2026, 10:00–12:00, display time",
 * "Monthly on the 1st and 15th, all day, display time · from 1 Oct 2026".
 */
export function ruleSummary(rule: RuleLike): string {
  const window = windowLabel(rule.start_time, rule.end_time)
  const zone = timezoneLabel(rule.timezone)
  const mask = rule.weekdays ?? ALL_DAYS
  let head: string
  switch (rule.recurrence) {
    case 'once': {
      const wd = rule.start_date ? isoWeekdayOf(rule.start_date) : null
      head = rule.start_date ? `Once on ${wd !== null ? `${WEEKDAY_SHORT[wd]} ` : ''}${formatRuleDate(rule.start_date)}` : 'Once (no date set)'
      return `${head}, ${window}, ${zone}`
    }
    case 'monthly_dom':
      head = `Monthly on ${daysOfMonthLabel(rule.days_of_month)}`
      if ((mask & ALL_DAYS) !== ALL_DAYS) head += ` (${weekdaysLabel(mask)} only)`
      head += `, ${window}`
      break
    default: {
      const days = weekdaysLabel(mask)
      const every = days === 'every day' ? 'Every day' : days === 'no days' ? 'Never (no weekdays selected)' : `Every ${days.replace(/s$/, '')}`
      head = every + (window === 'all day' ? ', all day' : ` ${window}`)
    }
  }
  const range =
    rule.start_date && rule.end_date
      ? ` · ${formatRuleDate(rule.start_date)} – ${formatRuleDate(rule.end_date)}`
      : rule.start_date
        ? ` · from ${formatRuleDate(rule.start_date)}`
        : rule.end_date
          ? ` · until ${formatRuleDate(rule.end_date)}`
          : ''
  return `${head}, ${zone}${range}`
}

export const RECURRENCE_LABELS: Record<ScheduleRecurrence, string> = {
  once: 'Once',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly_dom: 'Monthly (days of month)',
}
